import {css, StyleSheet} from 'aphrodite';
import React, {useEffect} from 'react';
import isEqual from 'react-fast-compare';

import {ContainerSummary} from 'constants/SampleConstants';
import * as InventoryUnitUtil from 'inventory/InventoryUnitUtil';
import {
  canProceedWithSamples,
  checkSamplesForStatusWarnings,
  SampleStatusWarning,
} from 'inventory/sample-status/SampleStatusWarning';
import {BaseSampleStatusWarningTrackingParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import NameTemplateInfoBanner from 'name-templates/NameTemplateInfoBanner';
import isBatchesEnabled from 'registry/IsBatchesEnabled';
import {getContainerLabelWithQuantity} from 'samples/SampleUtil';
import ChooseInputContainable from 'samples/transfers/ChooseInputContainable';
import ChooseInputContainer from 'samples/transfers/ChooseInputContainer';
import {
  TYPE as InputSampleType,
  IS_CONTAINABLE_TYPES,
  IS_CONTAINER_TYPES,
} from 'samples/transfers/input-sample/InputSampleConstants';
import {
  ChooseTransferInputPaneState,
  getConfirmedOutputContainers,
  getInputTagSchemaId,
  UpdateTransferModalStateHandler,
  UpdateTransferPaneSampleStatusStateHandler,
} from 'samples/transfers/TransferModalUtil';
import {willTransferApplyContainerNameTemplate} from 'samples/transfers/TransferUtil';
import Label from 'ui-library/bs/Label';
import useOnMount from 'ui-library/hooks/useOnMount';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {GenericEvent} from 'util/Types';

export interface Props {
  paneState: ChooseTransferInputPaneState;
  registryId?: string;
  sampleStatusWarningParams: BaseSampleStatusWarningTrackingParams & {
    shouldShowOutputWarnings: boolean;
  };
  onUpdateSampleStatusState: UpdateTransferPaneSampleStatusStateHandler;
  onUpdateModalState: UpdateTransferModalStateHandler;
}

export function getIncompatibleUnitsOutputContainers({
  inputType,
  inputContainer,
  selectedOutputContainers,
}: ChooseTransferInputPaneState): Array<ContainerSummary> {
  const inputTypeIsContainer = IS_CONTAINER_TYPES.includes(inputType);
  if (!inputTypeIsContainer || inputContainer == null) {
    return [];
  }
  const incompatibleUnitsOutputContainers = selectedOutputContainers.filter(
    (selectedOutputContainer) =>
      !InventoryUnitUtil.areUnitsCompatibleFromSymbols([
        inputContainer.quantity.units,
        selectedOutputContainer.quantity.units,
      ])
  );
  return incompatibleUnitsOutputContainers;
}

export function ChooseInputContainerValidationError({
  selectedInputContainer,
  incompatibleUnitsOutputContainers,
}: {
  selectedInputContainer: ContainerSummary;
  incompatibleUnitsOutputContainers: Array<ContainerSummary>;
}): JSX.Element {
  return (
    <div className="legacy-font-proxima-13">
      <p className="u-danger-color">
        {`Cannot transfer from selected container ${getContainerLabelWithQuantity(
          selectedInputContainer
        )} into ${incompatibleUnitsOutputContainers
          .map((container) => getContainerLabelWithQuantity(container))
          .join(', ')} because the units are incompatible.`}
      </p>
    </div>
  );
}

function ChooseInputPane({
  paneState,
  registryId,
  sampleStatusWarningParams,
  onUpdateSampleStatusState,
  onUpdateModalState,
}: Props): JSX.Element {
  useOnMount(() => {
    function checkForOutputWarnings(): void {
      const {shouldShowWarning, samples} = checkSamplesForStatusWarnings({
        samples: paneState.selectedOutputContainers,
      });
      const selectedSampleIds = samples?.map(({id}) => id) ?? [];
      // Only save selectedSampleIds if paneState.confirmedOutputContainerIds is null, which means we
      // haven't initialized those values. This enables switching between panes and preserving the previous
      // selection.
      const newOutputStatusState = {
        ...paneState.outputSampleStatusState,
        shouldShowStatusWarnings: shouldShowWarning,
        samples: samples ?? null,
        ...(paneState.confirmedOutputContainerIds == null && {selectedSampleIds}),
      };
      const newPaneState = {
        outputSampleStatusState: newOutputStatusState,
        ...(shouldShowWarning &&
          paneState.confirmedOutputContainerIds == null && {confirmedOutputContainerIds: selectedSampleIds}),
      };
      if (!isEqual(newOutputStatusState, paneState.outputSampleStatusState)) {
        onUpdateModalState({...newPaneState});
      }
    }

    if (paneState.selectedOutputContainers.length > 0 && sampleStatusWarningParams.shouldShowOutputWarnings) {
      checkForOutputWarnings();
    }
  });

  useEffect(() => {
    willTransferApplyContainerNameTemplate(
      getInputTagSchemaId(
        paneState.createAndRegisterBioentityArgs,
        paneState.inputBioentity,
        paneState.inputType
      ),
      paneState.inputType === InputSampleType.EXISTING_CONTAINER
        ? (paneState.inputContainer?.id ?? null)
        : null,
      getConfirmedOutputContainers(
        paneState.outputCells,
        paneState.outputContainerSummaries,
        paneState.confirmedOutputContainerIds,
        paneState.outputType
      )
    ).then((willApplyNameTemplate) => {
      onUpdateModalState({showNameTemplateBanner: willApplyNameTemplate});
    });
  }, [
    paneState.createAndRegisterBioentityArgs,
    paneState.inputBioentity,
    paneState.inputContainer,
    paneState.inputType,
    paneState.outputCells,
    paneState.outputContainerSummaries,
    paneState.confirmedOutputContainerIds,
    paneState.outputType,
    onUpdateModalState,
  ]);

  const setSelectedSampleIds = (sampleIds: Array<string>): void => {
    onUpdateSampleStatusState({selectedSampleIds: sampleIds}, 'outputSampleStatusState');
    onUpdateModalState({confirmedOutputContainerIds: sampleIds});
  };

  const setCanProceedOnSampleStatus = (canProceed: boolean): void => {
    onUpdateSampleStatusState({canProceedOnSampleStatus: canProceed}, 'outputSampleStatusState');
  };

  const possibleWarning = sampleStatusWarningParams.shouldShowOutputWarnings &&
    paneState.outputSampleStatusState.shouldShowStatusWarnings &&
    paneState.outputSampleStatusState.samples != null && (
      <SampleStatusWarning
        sampleIds={paneState.selectedOutputContainers.map(({id}) => id)}
        selectedSampleIds={paneState.outputSampleStatusState.selectedSampleIds}
        setSelectedSampleIds={setSelectedSampleIds}
        shouldProceed={paneState.outputSampleStatusState.canProceedOnSampleStatus}
        setShouldProceed={setCanProceedOnSampleStatus}
        trackingParams={sampleStatusWarningParams}
        samples={paneState.outputSampleStatusState.samples}
      />
    );

  function handleChangeInputType(e: GenericEvent): void {
    onUpdateModalState({inputType: e.target.value as InputSampleType});
  }

  const {
    createAndRegisterBioentityArgs,
    inputBioentity,
    inputContainer,
    inputType,
    inputSampleStatusState,
    showNameTemplateBanner,
  } = paneState;

  const isContainable = IS_CONTAINABLE_TYPES.includes(inputType);
  const isContainer = IS_CONTAINER_TYPES.includes(inputType);

  const incompatibleUnitsOutputContainers = getIncompatibleUnitsOutputContainers(paneState);

  const inputPaneContent = (
    <div>
      <div>
        <FieldLabel label="Source" />
        <div>
          <Label className="radio-inline radio-inline--labelAligned u-spacing-right">
            <input
              checked={isContainable}
              name="chooseInputPane-inputType"
              onChange={handleChangeInputType}
              type="radio"
              value={IS_CONTAINABLE_TYPES[0]}
            />
            {isBatchesEnabled() ? 'Add an entity or batch' : 'Add an entity'}
          </Label>
          <Label className="radio-inline radio-inline--labelAligned">
            <input
              checked={isContainer}
              name="chooseInputPane-inputType"
              onChange={handleChangeInputType}
              type="radio"
              value={IS_CONTAINER_TYPES[0]}
            />
            Transfer from existing container
          </Label>
        </div>
      </div>
      <div className="u-spacing-top">
        {isContainable ? (
          <ChooseInputContainable
            createAndRegisterBioentityArgs={createAndRegisterBioentityArgs}
            inputBioentity={inputBioentity}
            inputType={inputType}
            onUpdateModalState={onUpdateModalState}
          />
        ) : (
          <ChooseInputContainer
            container={inputContainer}
            errors={paneState.errors}
            registryId={registryId}
            inputSampleStatusState={inputSampleStatusState}
            sampleStatusWarningTrackingParams={sampleStatusWarningParams}
            onUpdateSampleStatusState={onUpdateSampleStatusState}
            onUpdateModalState={onUpdateModalState}
          />
        )}
      </div>

      {inputContainer != null && incompatibleUnitsOutputContainers.length > 0 && (
        <ChooseInputContainerValidationError
          selectedInputContainer={inputContainer}
          incompatibleUnitsOutputContainers={incompatibleUnitsOutputContainers}
        />
      )}
      {showNameTemplateBanner && (
        <div className={css(styles.infoBanner)}>
          <NameTemplateInfoBanner
            itemDescription="container"
            isPlural={
              getConfirmedOutputContainers(
                paneState.outputCells,
                paneState.outputContainerSummaries,
                paneState.confirmedOutputContainerIds,
                paneState.outputType
              ).length > 1
            }
          />
        </div>
      )}
    </div>
  );

  return (
    <>
      {possibleWarning}
      {canProceedWithSamples(
        paneState.outputSampleStatusState.selectedSampleIds,
        sampleStatusWarningParams.shouldShowOutputWarnings &&
          paneState.outputSampleStatusState.shouldShowStatusWarnings,
        paneState.outputSampleStatusState.canProceedOnSampleStatus
      ) && inputPaneContent}
    </>
  );
}

const styles = StyleSheet.create({
  infoBanner: {
    marginTop: 15,
  },
});

export default ChooseInputPane;
